import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { Spinner } from '@aibuild/ui';
import { usePageTitle } from '@contexts/usePageTitle';
import { localized } from '../appRunView';
import { ViewSnapshotProvider } from '../viewSnapshot';
import BuilderUnavailable from './BuilderUnavailable';
import { useUserAppDraft } from './useAppBuilder';
import BuilderWorkspace from './workspace/BuilderWorkspace';
import { LoadingWrap } from './AppBuilderPage.styled';

const GONE = new Set([403, 404]);

// A refetch that fails keeps the last draft, so an app deleted or taken away meanwhile has to be told apart.
const isGone = (error: unknown): boolean => GONE.has((error as AxiosError | null)?.response?.status ?? 0);

const AppBuilderPage: React.FC = () => {
  const intl = useIntl();
  const { appKey } = useParams<{ appKey: string }>();
  const draft = useUserAppDraft(appKey);
  usePageTitle(
    draft.data
      ? localized(intl, draft.data.manifest.name)
      : intl.formatMessage({ id: 'apps.builder.title', defaultMessage: 'App Builder' }),
    intl.formatMessage({
      id: 'apps.builder.help',
      defaultMessage:
        'Describe the job and the assistant builds the app with you. Try it here, then publish it for your team.',
    }),
  );
  const { refetch } = draft;
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  if (draft.isLoading) {
    return (
      <LoadingWrap>
        <Spinner size="lg" />
      </LoadingWrap>
    );
  }
  if (!draft.data || isGone(draft.error)) return <BuilderUnavailable appKey={appKey} />;
  return (
    <ViewSnapshotProvider>
      <BuilderWorkspace draft={draft.data} onReload={reload} />
    </ViewSnapshotProvider>
  );
};

export default AppBuilderPage;
